import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import {
  reissueLineChannelAccessToken,
  type ReissueLineChannelAccessTokenRequest,
} from 'api/line';
import { lineStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import { toErrorMessage } from 'hooks/utils';

type UseReissueLineChannelAccessTokenOptions = {
  onSuccess?: (accessToken: string) => void;
};

type UseReissueLineChannelAccessTokenResult = {
  reissue: (
    lineChannelId: number,
    request: ReissueLineChannelAccessTokenRequest,
  ) => void;
  isSubmitting: boolean;
  error: string | undefined;
};

export function useReissueLineChannelAccessToken(
  options: UseReissueLineChannelAccessTokenOptions = {},
): UseReissueLineChannelAccessTokenResult {
  const { user } = useAuth();
  const strings = lineStrings.edit;

  const [error, setError] = useState<string | undefined>();

  const mutation = useMutation({
    mutationFn: async (params: {
      lineChannelId: number;
      request: ReissueLineChannelAccessTokenRequest;
    }) => {
      if (!user) {
        throw new Error(strings.errors.unauthorized);
      }
      return reissueLineChannelAccessToken(
        params.lineChannelId,
        params.request,
      );
    },
    onSuccess: (response) => {
      setError(undefined);
      options.onSuccess?.(response.accessToken);
    },
    onError: (caught) => {
      setError(toErrorMessage(caught, strings.errors.reissueFailed));
    },
  });

  const reissue = (
    lineChannelId: number,
    request: ReissueLineChannelAccessTokenRequest,
  ) => {
    setError(undefined);
    mutation.mutate({ lineChannelId, request });
  };

  return {
    reissue,
    isSubmitting: mutation.isPending,
    error,
  };
}
